import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useCustomEventListener } from "react-custom-events";
import AddressBookApi from "../../../api/AddressBook";
import AddAddressModal from "../../../components/modals/AddAddressModal";
import { DashboardLayout } from "../../../components/organisms/Layout";
import { FormModal } from "../../../components/organisms/FormModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import DeleteModal from "../../../components/modals/DeleteModal";
import { addIcon } from "../../../assets/images";
import Table from "../../../components/atoms/Tables/Table";
import SearchBar from "../../../components/atoms/SearchBar";
import useQuery from "../../../hooks/useQuery";
import debounce from "lodash/debounce";
import PaginationView from "../../../components/molecules/Pagination";

const AddressBook = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [addressToEdit, setAddressToEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchAddress, setSearchAddress] = useState("");
  const [filteredAddress, setFilteredAddress] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 50);

  useEffect(() => {
    document.title = "HeyHire Portal-Address Book";
  }, []);

  useEffect(() => {
    loadAddressBook();
  }, [query]);

  useCustomEventListener("closeModal", () => {
    setShowModal(false);
    setAddressToEdit(null);
    setAddressToDelete(null);
  });

  useCustomEventListener("dataChanged", () => {
    loadAddressBook();
  });

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      // Create a new object from current search params
      const newParams = { ...Object.fromEntries(searchParams) };
      
      // Reset to page 1 when searching
      newParams.page = 1;
      
      // Add or remove search parameter based on value
      if (txt && txt.trim() !== '') {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }
      
      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const searchAddressName = (txt) => {
    setSearchAddress(txt);
    debouncedUpdateSearch(txt);
  };

  const loadAddressBook = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";
    
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = pageData + `&limit=${limit}` + searchData;
    
    // Update the search input field when loading from URL
    setSearchAddress(search);
    
    AddressBookApi.getAll(queryData)
      .then((response) => {
        setFilteredAddress(response.data.data);
        setMetaData(response.data.meta);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader="Address Book"
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchAddress}
                  placeholder="Search by address name, city or state..."
                  searchFunction={(value) => searchAddressName(value)}
                />
              </div>
            </div>
          }
          maxHeight="calc(100vh - 250px)"
          headers={
            !loading && filteredAddress?.length === 0
              ? ["No data available"]
              : [
                  t("common.name"),
                  t("common.address"),
                  t("common.address2"),
                  t("common.city"),
                  t("common.state"),
                  t("common.zip"),
                  "Actions"
                ]
          }
            data={filteredAddress?.map((address, index) => (
              <tr
                key={index}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="font-semibold text-gray-900">
                    {address?.name}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {address?.address}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {address?.address2 || "-"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {address?.city}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {address?.state}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {address?.zip}
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                  <div className="flex items-center gap-4">
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                      onClick={() => navigate(`${address.id}/edit`)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                      onClick={() => setAddressToDelete(address.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          buttonData={
              <button
                onClick={() => navigate("add")}
                className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
              >
                <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
                Add Address
              </button>
            }
          />
          <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
            {!loading && metaData?.total > 0 && (
              <PaginationView
                current={metaData?.current_page}
                next={metaData?.to}
                pageSize={metaData?.per_page}
                total={metaData?.total}
                onChange={(currentPage, pageSize) => {
                  setPagelimit(pageSize);
                  // Create a new object from current search params
                  const newParams = { ...Object.fromEntries(searchParams) };
                  
                  newParams.page = currentPage;
                  newParams.limit = pageSize;
                  
                  // Only include search if it has a value
                  if (searchAddress && searchAddress.trim() !== '') {
                    newParams.search = searchAddress.trim();
                  } else {
                    delete newParams.search;
                  }
                  
                  setSearchParams(newParams);
                }}
              />
            )}
          </div>
        </div>
      {showModal && <AddAddressModal />}
      <FormModal
        visible={false}
        onClose={() => setShowModal(false)}
        title={
          addressToEdit
            ? t("address-book.edit-address")
            : t("address-book.add-address")
        }
      >
        <AddAddressModal />
      </FormModal>

      {addressToDelete && (
        <DeleteModal
          title="Delete Address"
          name="Address"
          deleteRequest={() => AddressBookApi.delete(addressToDelete)}
          onSuccess={() => {
            setAddressToDelete(null);
            loadAddressBook();
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default AddressBook;
